/** REST client. Browser auth uses the HttpOnly session cookie plus a CSRF header on writes. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;
export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const isBinary =
    body instanceof Blob || body instanceof ArrayBuffer || body instanceof Uint8Array;
  const headers: Record<string, string> = {};
  if (body !== undefined)
    headers['content-type'] = isBinary ? 'application/octet-stream' : 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : isBinary ? (body as BodyInit) : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as {
    error?: { code: string; message: string; details?: unknown };
  } | null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.error?.code ?? 'ERROR',
      data?.error?.message ?? res.statusText,
      data?.error?.details,
    );
  }
  return data as T;
}

/** Raw request used by the E2EE transport (needs status codes, not exceptions). */
export async function rawApi(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ status: number; body: unknown }> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return {
    status: res.status,
    body: res.status === 204 ? null : await res.json().catch(() => null),
  };
}

export async function uploadFile(
  file: Blob,
  filename: string,
  opts: { encrypted?: boolean; channelId?: string; contentType?: string } = {},
) {
  const { UploadTicketSchema } = await import('./schemas');
  const ticket = UploadTicketSchema.parse(
    await api('POST', '/attachments', {
      filename,
      contentType: opts.contentType ?? (file.type || 'application/octet-stream'),
      size: file.size,
      encrypted: opts.encrypted ?? false,
      ...(opts.channelId ? { channelId: opts.channelId } : {}),
    }),
  );
  return api<import('@comms/shared').Attachment>(
    'PUT',
    ticket.uploadUrl.replace(/^\/api\/v1/, ''),
    file,
  );
}
